import { type Extensions } from "@tiptap/core";
import { Skeleton } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { v4 as uuid4 } from "uuid";

import { cn } from "@btnet/utilities-core";

import { BdsButton, BdsIconX, BdsText } from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { DiscussionsLinkedTypes } from "types/enum";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";

import { type IBTEditorChatInterfaceProps } from "commonComponents/btWrappers/editor/BTEditorChat/BTEditorChatInterface.types";
import { BTEditorContent } from "commonComponents/btWrappers/editor/core/BTEditorContent/BTEditorContent";
import {
    EDITOR_CONTAINER_ATTRIBUTE,
    getEditorControlBreakpointFromWidth,
} from "commonComponents/btWrappers/editor/core/editor.utils";
import { InputRecaptureHack } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/InputRecaptureHack/InputRecaptureHack";
import { type EditorControlBreakpointNames } from "commonComponents/btWrappers/editor/core/EditorControlBar/EditorControlBar.types";
import { EditorTwoStageControlBar } from "commonComponents/btWrappers/editor/core/EditorTwoStageControlBar/EditorTwoStageControlBar";
import { useBTEditor } from "commonComponents/btWrappers/editor/core/useBTEditor";
import { type EditorContent } from "commonComponents/btWrappers/editor/editor.types";
import { formatDate } from "commonComponents/utilities/DateDisplay/DateDisplay";

import "./BTEditorChatInterface.less";

/**
 * The standard UX interface for the BTEditor, including the control bar and the editor. Also
 * handles calculating the responsive breakpoint size for the control bar. Do not use
 * this component directly. Instead, use BTEditor or BTEditorBasic, or compose your own interface making use of the
 * useBTEditor (with architecture/UX approval)
 */
export default function BTEditorChatInterface<
    FormValues = undefined,
    ActionType extends string | undefined = string,
>({
    id,
    "data-testid": dataTestId,
    value,
    onChange,
    onBlur,
    onFocus,
    readOnly,
    hideImages,
    autoFocus,
    debouncingContext,
    editorControlSize = "auto",
    extensions,
    onSendChat,
    style,
    onClearReplyContent,
    addReplyToListener,
    replyContentClassName,
}: IBTEditorChatInterfaceProps<FormValues, ActionType>) {
    const editor = useBTEditor(
        id,
        extensions,
        !!readOnly,
        value,
        "inlineControlsOnly",
        onChange,
        onBlur,
        autoFocus,
        debouncingContext,
        onFocus,
    );
    const globalUserId = useRequiredContext(UserInfoContext).globalUserId;

    const [currentBreakpoint, setCurrentBreakpoint] = useState<EditorControlBreakpointNames | null>(
        editorControlSize === "auto" ? null : editorControlSize,
    );
    const [, setHeaderParentRef] = useResizeObserver((entry) => {
        if (entry && editorControlSize === "auto") {
            setCurrentBreakpoint(getEditorControlBreakpointFromWidth(entry.contentRect.width));
        }
    });

    // We need to know if the control bar is sticking to adjust the z-index for overlay interaction
    // This ref will be out of view when it is stuck
    const { ref: topRef, inView } = useInView({ threshold: 1 });
    const controlBarStuck = !inView;

    addReplyToListener?.((comment) => {
        if (comment.addedById === globalUserId || isUnsupportedEntity(comment.sourceTypeId)) {
            // Can't self mention or mention on unsupported entities
            // Clearing content erase previous mention when the user immediately replies to a 2nd comment before sending the first
            editor?.tiptapEditor.chain().clearContent().focus().run();
            return;
        }
        // Insert a mention any time a reply is made
        requestAnimationFrame(() => {
            editor?.tiptapEditor
                .chain()
                // Clearing content to prevent muliple identical mentions getting inserted if a user spams the reply button
                .clearContent()
                .insertContentAt(0, [
                    {
                        type: "mentionUser",
                        attrs: {
                            id: comment.addedById,
                            label: comment.userFullName,
                            mentionId: uuid4(),
                        },
                    },
                    {
                        type: "text",
                        text: " ",
                    },
                ])
                .focus()
                .run();
        });
    });

    if (!editor) {
        return <Skeleton.Input size="large" />;
    }

    const controls = !readOnly && (
        <>
            <div ref={topRef} />
            <InputRecaptureHack
                ref={setHeaderParentRef}
                className={cn("BTEditorChatInterface--ControlBarHeader HasBdsStyles", {
                    Stuck: controlBarStuck,
                })}
                editor={editor}
            >
                {!currentBreakpoint && <Skeleton />}
                {currentBreakpoint && (
                    <>
                        <div className="BTEditorChatInterface--ControlBarHeader--Controls">
                            <EditorTwoStageControlBar
                                data-testid={`${dataTestId}-controls`}
                                editorSize={currentBreakpoint}
                                editor={editor}
                                dropdownPlacement="topCenter"
                            />
                        </div>
                    </>
                )}
            </InputRecaptureHack>
        </>
    );

    return (
        <>
            {controls}
            <div
                style={style}
                className={cn("BTEditorChatInterface HasBdsStyles", {
                    "ant-input": !readOnly,
                    Editable: !readOnly,
                    HasReplyContent: value.replyContent !== undefined,
                })}
                data-testid={dataTestId}
                {...{ [EDITOR_CONTAINER_ATTRIBUTE]: "true" }}
                onKeyDownCapture={(e) => {
                    if (
                        e.key === "Backspace" &&
                        value.replyContent &&
                        editor.tiptapEditor.state.selection.from === 1
                    ) {
                        onClearReplyContent?.();
                    } else if (
                        e.key === "Enter" &&
                        e.ctrlKey &&
                        editor.editorState.mode === "inlineControlsOnly"
                    ) {
                        // Our editor absorbs all enter hotkey detection, so we do Ctrl+Enter detection here
                        onSendChat?.();
                    }
                }}
            >
                {value.replyContent && (
                    <ReplyContent
                        extensions={extensions}
                        value={value}
                        currentBreakpoint={currentBreakpoint}
                        replyContentClassName={replyContentClassName}
                        onClearReplyContent={onClearReplyContent}
                    />
                )}
                <div
                    className={cn({
                        "BTEditorChatInterface--EditorContentEditable": !readOnly,
                    })}
                >
                    <BTEditorContent
                        id={String(id)}
                        data-testid={`${dataTestId}-content`}
                        editorSize={currentBreakpoint}
                        editor={editor}
                        readOnly={readOnly}
                        hideImages={hideImages}
                        linkOverlayPlacements={["top-start", "top", "top-end"]}
                    />
                </div>
            </div>
        </>
    );
}

/**
 * This type is needed to preserve the type arguments to the component. When React `lazy` is used,
 * component type arguments are stripped away, causing issues. This helps restore them as a workaround
 */
export type BTEditorChatInterfaceComponentType = typeof BTEditorChatInterface;

interface IReplyContentProps {
    extensions: Extensions;
    value: EditorContent;
    currentBreakpoint: EditorControlBreakpointNames | null;
    replyContentClassName?: string;
    onClearReplyContent?: () => void;
}

function ReplyContent({
    value,
    extensions,
    currentBreakpoint,
    replyContentClassName,
    onClearReplyContent,
}: IReplyContentProps) {
    const replyEditor = useBTEditor(
        "reply",
        extensions,
        true,
        value.replyContent!,
        "inlineControlsOnly",
        () => {},
        () => {},
    );

    useEffect(() => {
        requestAnimationFrame(() => {
            const replyContent = value.replyContent?.content?.toString() ?? "";
            replyEditor?.tiptapEditor
                .chain()
                .clearContent()
                .insertContentAt(1, replyContent)
                .focus()
                .run();
        });
    }, [value.replyContent?.content, replyEditor?.tiptapEditor]);

    return (
        <div className={cn("ReplyContent", replyContentClassName)}>
            <CommentHeader
                commentAuthor={value.replyContent!.userFullName ?? ""}
                commentDate={moment(value.replyContent!.commentDate)}
                onClose={onClearReplyContent}
            />
            <BTEditorContent
                id="reply"
                data-testid="reply"
                editorSize={currentBreakpoint}
                editor={replyEditor!}
                readOnly
                hideImages
                linkOverlayPlacements={["top-start", "top", "top-end"]}
            />
        </div>
    );
}

interface ICommentHeaderProps {
    commentAuthor: string;
    commentDate: moment.Moment;
    onClose?: () => void;
}

export function CommentHeader({ commentAuthor, commentDate, onClose }: ICommentHeaderProps) {
    const builderInfo = useRequiredContext(BuilderInfoContext);
    return (
        <div className="flex flex-row gap-2 items-center justify-between w-full">
            <div className="flex flex-row justify-between gap-2 items-center flex-wrap w-full">
                <BdsText size="distinct-sm" text={commentAuthor} />
                {commentDate && (
                    <BdsText
                        className="CommentDateDisplay"
                        text={formatDate(commentDate, builderInfo.locale.timeLocale, {
                            showTime: true,
                            showYear: true,
                            hideCurrentYear: true,
                            showDayOfWeek: true,
                            showDate: true,
                            showMonth: true,
                        })}
                    />
                )}
            </div>
            {onClose && (
                <BdsButton
                    displayType="tertiary"
                    icon={<BdsIconX />}
                    ariaLabel="Cancel reply"
                    id="cancelReply"
                    onClick={onClose}
                    testid="cancelReply"
                />
            )}
        </div>
    );
}

export function isUnsupportedEntity(linkedType?: DiscussionsLinkedTypes) {
    return linkedType === DiscussionsLinkedTypes.Selections ||
        linkedType === DiscussionsLinkedTypes.ChangeOrders ||
        linkedType === DiscussionsLinkedTypes.PurchaseOrders ||
        linkedType === DiscussionsLinkedTypes.DailyLogs ||
        linkedType === DiscussionsLinkedTypes.Warranty ||
        linkedType === DiscussionsLinkedTypes.ScheduleItems ||
        linkedType === DiscussionsLinkedTypes.ToDos ||
        linkedType === DiscussionsLinkedTypes.OwnerPayments ||
        linkedType === DiscussionsLinkedTypes.LegacyBills ||
        linkedType === DiscussionsLinkedTypes.Bills ||
        linkedType === DiscussionsLinkedTypes.Bids ||
        linkedType === DiscussionsLinkedTypes.ClientUpdate ||
        linkedType === DiscussionsLinkedTypes.Deposit ||
        linkedType === DiscussionsLinkedTypes.CreditMemos ||
        linkedType === DiscussionsLinkedTypes.Receipts ||
        linkedType === DiscussionsLinkedTypes.Task
        ? undefined
        : linkedType;
}
